import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const FALLBACK = {
  campaign_image: "/brand/campaign-summer.jpg",
  campaign_eyebrow: "The Summer Campaign · 2026",
  campaign_title: "Sun, salt & the whole house.",
  campaign_text: "Lightweight tailoring, resort knits and the fragrances that carry them — styled as one seamless season.",
  campaign_cta_label: "Shop the look",
  campaign_cta_link: "/lookbook",
};

export default function CampaignBanner() {
  const [s, setS] = useState(FALLBACK);
  const [next, setNext] = useState(null);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        const d = r.data || {};
        setS({
          campaign_image: d.campaign_image || FALLBACK.campaign_image,
          campaign_eyebrow: d.campaign_eyebrow || FALLBACK.campaign_eyebrow,
          campaign_title: d.campaign_title || FALLBACK.campaign_title,
          campaign_text: d.campaign_text || FALLBACK.campaign_text,
          campaign_cta_label: d.campaign_cta_label || FALLBACK.campaign_cta_label,
          campaign_cta_link: d.campaign_cta_link || FALLBACK.campaign_cta_link,
        });
        const today = new Date().toISOString().split("T")[0];
        const upcoming = (d.campaign_queue || [])
          .filter((c) => c && c.start_date && c.start_date > today)
          .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
        if (upcoming) {
          const days = Math.max(1, Math.ceil((new Date(upcoming.start_date) - new Date(today)) / 86400000));
          setNext({ days, title: upcoming.campaign_eyebrow || upcoming.campaign_title || "A new campaign" });
        }
      })
      .catch(() => {});
  }, []);

  const internal = (s.campaign_cta_link || "/lookbook").startsWith("/");
  const btnClass =
    "group mt-8 inline-flex items-center gap-2.5 bg-gold-light text-ink px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-ivory transition-colors";

  return (
    <section data-testid="campaign-banner" className="relative h-[70vh] min-h-[460px] w-full overflow-hidden">
      <img src={s.campaign_image} alt={s.campaign_eyebrow} className="absolute inset-0 h-full w-full object-cover object-[72%_center]" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/50 to-transparent" />
      {next && (
        <div data-testid="campaign-countdown" className="absolute top-5 right-5 z-10 flex items-center gap-2 bg-obsidian/70 backdrop-blur border border-gold/40 px-4 py-2 text-ivory">
          <span className="w-1.5 h-1.5 rounded-full bg-gold-light animate-pulse" />
          <span className="text-[9px] tracking-[0.3em] uppercase text-gold-light">{next.title} · drops in {next.days} day{next.days === 1 ? "" : "s"}</span>
        </div>
      )}
      <div className="relative h-full max-w-[1400px] mx-auto px-8 lg:px-16 flex items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl text-ivory"
        >
          <p data-testid="campaign-eyebrow" className="text-[10px] tracking-[0.5em] uppercase text-gold-light">{s.campaign_eyebrow}</p>
          <h2 data-testid="campaign-title" className="mt-5 font-serif text-4xl md:text-5xl tracking-tight leading-[1.02]">{s.campaign_title}</h2>
          <p className="mt-5 text-ivory/70 text-sm md:text-base leading-relaxed max-w-md">{s.campaign_text}</p>
          {internal ? (
            <Link to={s.campaign_cta_link} data-testid="campaign-shop-look" className={btnClass}>
              {s.campaign_cta_label} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          ) : (
            <a href={s.campaign_cta_link} data-testid="campaign-shop-look" className={btnClass}>
              {s.campaign_cta_label} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          )}
        </motion.div>
      </div>
    </section>
  );
}
